import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUp, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

interface BreadcrumbItem {
  label: string;
  to: string;
}

interface ExplorerBarProps {
  breadcrumbs: BreadcrumbItem[];
}

const ExplorerBar: React.FC<ExplorerBarProps> = ({ breadcrumbs }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [historyIndex, setHistoryIndex] = useState<number>(() => window.history.state?.idx ?? 0);
  const [maxIndex, setMaxIndex] = useState<number>(() => {
    const stored = sessionStorage.getItem("navMaxIdx");
    return stored ? parseInt(stored, 10) : window.history.state?.idx ?? 0;
  });

  useEffect(() => {
    const idx = window.history.state?.idx ?? 0;
    setHistoryIndex(idx);
    setMaxIndex((prev) => {
      const next = Math.max(prev, idx);
      sessionStorage.setItem("navMaxIdx", String(next));
      return next;
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handlePopState = () => {
      const idx = window.history.state?.idx ?? 0;
      setHistoryIndex(idx);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex < maxIndex;

  const upTarget = useMemo(() => {
    if (breadcrumbs.length < 2) return null;
    return breadcrumbs[breadcrumbs.length - 2]?.to || null;
  }, [breadcrumbs]);

  return (
    <div className="flex items-center gap-3 bg-[#1f1f1f] border border-[#2f2f2f] rounded-xl px-3 py-2 shadow-lg shadow-black/10">
      <button
        onClick={() => canGoBack && navigate(-1)}
        disabled={!canGoBack}
        className={`p-2 rounded-lg transition-colors ${canGoBack ? "text-[#E0E0E0] hover:bg-[#2a2a2a]" : "text-[#555] cursor-not-allowed"}`}
        aria-label={t("explorer.back")}
      >
        <ArrowLeft size={16} />
      </button>
      <button
        onClick={() => canGoForward && navigate(1)}
        disabled={!canGoForward}
        className={`p-2 rounded-lg transition-colors ${canGoForward ? "text-[#E0E0E0] hover:bg-[#2a2a2a]" : "text-[#555] cursor-not-allowed"}`}
        aria-label={t("explorer.forward")}
      >
        <ArrowRight size={16} />
      </button>
      <button
        onClick={() => upTarget && navigate(upTarget)}
        disabled={!upTarget}
        className={`p-2 rounded-lg transition-colors ${upTarget ? "text-[#E0E0E0] hover:bg-[#2a2a2a]" : "text-[#555] cursor-not-allowed"}`}
        aria-label={t("explorer.up")}
      >
        <ArrowUp size={16} />
      </button>

      <div className="flex items-center gap-1 flex-1 overflow-hidden">
        {breadcrumbs.map((item, index) => (
          <React.Fragment key={item.to}>
            <Link
              to={item.to}
              className="text-sm text-[#E0E0E0] hover:text-[#C8102E] transition-colors truncate"
            >
              {item.label}
            </Link>
            {index < breadcrumbs.length - 1 && <ChevronRight size={14} className="text-[#666]" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ExplorerBar;
